import { Listing, type ListingFilterValues } from "@verde/compose-ui";
import {
  type FundOperationItem,
  useFetchDashboardFundCount,
  useFetchFundOperations,
  useFetchInvestmentFunds,
} from "@verde/entities";
import {
  DocumentClipboardIcon,
  EditIcon,
  EllipsisHorizontalIcon,
  ExternalLinkIcon,
  LessIcon,
  PauseIcon,
  PlusIcon,
  RefreshIcon,
} from "@verde/icons";
import { People } from "@verde/modules";
import {
  Combobox,
  Copyable,
  IconButton,
  Link,
  Menu,
  TagButton,
} from "@verde/ui-core";
import { dayjs, formatDate, formatToBRL, formatToCPF } from "@verde/utils";
import { useEffect, useMemo, useReducer, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { tv } from "tailwind-variants";
import MonitoringRefreshDialog from "../components/monitoring-refresh-dialog";
import OperationDrawer from "../components/operation-drawer";
import OperationFinishedDialog from "../components/operation-finished-dialog";
import OperationForm from "../components/operation-form/operation-form";

const TABS = [
  {
    code: "monitoring",
    label: "Monitorado",
  },
  {
    code: "completed",
    label: "Concluídas",
  },
  {
    code: "operations",
    label: "Todas",
  },
];

export const operationsListStyles = tv({
  slots: {
    rating: "grid items-center h-8 w-10 rounded-[24px] border-neutral-light-3",
  },
});

export default function ListOperations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, isLoading } = useFetchInvestmentFunds();
  const count = useFetchDashboardFundCount();

  const searchByFund = searchParams.get("by_fund");
  const searchCriteriaName = searchParams.get("by_criteria[name]");
  const searchCriteriaWeight = searchParams.get("by_criteria=[weight]");
  const [params, setParams] = useState<ListingFilterValues>({
    by_status: "MONITORING",
  });

  const {
    data: operationsData,
    isLoading: operationsLoading,
    refetch,
  } = useFetchFundOperations(params);

  const [currentTab, setCurrentTab] = useState(TABS[0].code);

  const [isOpenFinishedOperation, toggleFinishedOperation] = useReducer(
    (value) => !value,
    false,
  );
  const [isEditOperation, toggleEditOperation] = useReducer(
    (value) => !value,
    false,
  );
  const [isOpenDrawer, toggleOpenDrawer] = useReducer((value) => !value, false);

  const [refreshDialogOpen, toggleRefreshDialogOpen] = useReducer(
    (state) => !state,
    false,
  );

  const [selectedOperation, setSelectedOperation] = useState<
    FundOperationItem | undefined
  >(undefined);

  const tableData = useMemo(() => {
    return {
      loading: operationsLoading,
      onRowClick: ({ data }: { data?: FundOperationItem }) => {
        setSelectedOperation(data);
        toggleOpenDrawer();
      },
      header: {
        rating: {
          render: "",
          styles: {
            body: "w-10",
          },
        },
        client: {
          render: "Cliente",
          styles: {
            body: "w-[25%]",
          },
        },
        funding: {
          render: "Financiamento",
          styles: {
            body: "w-[25%]",
          },
        },
        due_at: {
          render: "Data Vencimento",
          styles: {
            body: "w-[25%]",
          },
        },
        empty: {
          render: "",
          styles: {
            body: "w-[73px]",
          },
        },

        actions: {
          render: "",
        },
      },
      data: operationsData?.data.map((operation) => {
        const { icon, tag } = People.VariantByRiskRange({
          situation: operation.client.rating_situation,
        });

        const renderClientInfo = () => (
          <div className="grid gap-1">
            <p>
              {operation?.client.name ? (
                <Copyable>{operation.client.name}</Copyable>
              ) : (
                "-"
              )}
            </p>
            <p className="text-sm text-neutral-dark-1">
              {operation?.client.document_number ? (
                <Copyable>
                  {formatToCPF(operation.client.document_number)}
                </Copyable>
              ) : (
                "-"
              )}
            </p>
          </div>
        );

        const renderFunding = () => (
          <div className="grid gap-1">
            <p>{formatToBRL(operation.operation_value)}</p>

            <Link
              className="text-sm font-normal text-neutral-dark-1"
              onClick={() => {
                navigate(`/fundos/${operation.investment_fund.id}`);
              }}
            >
              {operation.investment_fund.name}
            </Link>
          </div>
        );

        const renderDate = (date: Date | null | string, fallback: string) =>
          date ? (
            formatDate(date, { date: "long" })
          ) : (
            <p className="text-sm text-neutral-dark-1">{fallback}</p>
          );

        const renderActions = () => (
          <div className="flex items-center gap-4">
            <div className="h-6 w-[1px] bg-neutral-light-3" />
            <Menu
              size="sm"
              side="bottom"
              items={[
                {
                  text: "Concluir monitoramento",
                  onClick: () => {
                    setSelectedOperation(operation);
                    toggleFinishedOperation();
                  },
                  icon: <PauseIcon />,
                },
                {
                  text: "Abrir fundo",
                  onClick: () => {
                    navigate(`/fundos/${operation.investment_fund.id}`);
                  },
                  icon: <ExternalLinkIcon />,
                },
                {
                  text: "Editar",
                  onClick: () => {
                    setSelectedOperation(operation);
                    toggleEditOperation();
                  },
                  icon: <EditIcon />,
                },
              ]}
            >
              <IconButton>
                <EllipsisHorizontalIcon />
              </IconButton>
            </Menu>
          </div>
        );

        return {
          data: operation,
          rating: (
            <div
              className={`grid items-center h-8 w-10 rounded-[24px] border-neutral-light-3 justify-center border ${tag}`}
            >
              {icon || <LessIcon className="h-2 w-2" />}
            </div>
          ),
          client: renderClientInfo(),
          funding: renderFunding(),
          due_at: renderDate(operation.due_date, "- Não disponível -"),
          actions: renderActions(),
        };
      }),
    };
  }, [operationsData?.data, navigate, operationsLoading]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (searchByFund) {
      setParams((state) => ({
        ...state,
        by_fund: searchByFund,
      }));
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (searchCriteriaName && searchCriteriaWeight && searchByFund) {
      setParams((state) => ({
        ...state,
        by_fund: searchByFund,
        by_status: "MONITORING",
        "by_criteria[name]": searchCriteriaName,
        "by_criteria[weight]": searchCriteriaWeight,
      }));
    }
  }, []);

  return (
    <>
      <Listing
        filters={({ form }) => {
          return (
            <div className="flex gap-4">
              <Controller
                name="by_due_month"
                control={form.control}
                render={({ field }) => (
                  <Combobox
                    {...field}
                    placeholder="Mês"
                    items={Array.from({ length: 12 }, (_, month) =>
                      dayjs().month(month).format("MMMM"),
                    ).map((month, index) => ({
                      value: `${dayjs().get("year")}-${(index + 1).toString().padStart(2, "0")}`,
                      label: month,
                    }))}
                  />
                )}
              />

              <Controller
                name="by_fund"
                control={form.control}
                render={({ field }) => (
                  <Combobox
                    {...field}
                    placeholder="Fundo"
                    loading={isLoading}
                    items={data?.data.map(({ id, name }) => ({
                      value: id,
                      label: name,
                    }))}
                  />
                )}
              />
            </div>
          );
        }}
        heading={({ form }) => {
          return {
            title: "Monitoramento",
            icon: <DocumentClipboardIcon />,
            children: (
              <div className="flex w-full items-center justify-between gap-1 rounded bg-neutral-light-pure p-4">
                <div className="flex w-full justify-between">
                  <div className="flex gap-4">
                    {TABS?.map((tab) => (
                      <div
                        key={`tab-${tab.code}-list`}
                        className="relative flex flex-col items-center"
                      >
                        <TagButton
                          key={`fund-status-tab-${tab.code}`}
                          onClick={() => {
                            setCurrentTab(tab.code);
                            tab.code !== "client"
                              ? form.setValue(
                                  "by_status",
                                  tab.code.toUpperCase(),
                                )
                              : form.reset({});
                          }}
                          selected={currentTab === tab.code}
                          unit={count.data?.[`${tab.code}`]?.total || undefined}
                        >
                          {tab.label}
                        </TagButton>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ),
            actions: {
              primary: {
                icon: <PlusIcon />,
                label: "Nova operação",
                onClick: toggleEditOperation,
              },
              secondary: {
                icon: <RefreshIcon />,
                label: "Atualizar Raio-X",
                onClick: toggleRefreshDialogOpen,
              },
            },
          };
        }}
        meta={operationsData?.meta}
        onFilterChange={setParams}
        table={tableData}
      />

      {refreshDialogOpen && (
        <MonitoringRefreshDialog onClose={toggleRefreshDialogOpen} />
      )}

      {selectedOperation && (
        <OperationDrawer
          open={isOpenDrawer}
          operation={selectedOperation}
          onClose={() => {
            setSelectedOperation(undefined);
            toggleOpenDrawer();
          }}
        />
      )}

      <OperationFinishedDialog
        open={isOpenFinishedOperation}
        operationId={selectedOperation?.id}
        onClose={() => {
          toggleFinishedOperation();
          setSelectedOperation(undefined);
        }}
      />

      <OperationForm
        open={isEditOperation}
        operation={selectedOperation}
        onClose={() => {
          toggleEditOperation();
          setSelectedOperation(undefined);
          refetch();
        }}
      />
    </>
  );
}
